/**
 * JournalEntryModal Component
 * Modal for creating/editing journal entries
 */

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Radio,
  RadioGroup,
  Stack,
  HStack,
  VStack,
  Badge,
  IconButton,
  useColorModeValue,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  SimpleGrid,
  Box,
  Divider,
  Checkbox,
  Text,
} from "@chakra-ui/react";
import { toast } from "sonner";
import React, { useState, useEffect } from "react";
// Types
import { JournalEntry, JournalEntryType, JournalEntryFormData } from "../../../../types/journal";
// Icons
import { AiOutlineClose } from "react-icons/ai";

interface JournalEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  entry?: JournalEntry | null;
  onSave: (data: JournalEntryFormData) => void;
}

const JournalEntryModal: React.FC<JournalEntryModalProps> = ({
  isOpen,
  onClose,
  entry,
  onSave,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const bgColor = useColorModeValue("white", "#151517");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("#575757", "white");
  const labelColor = useColorModeValue("gray.500", "gray.400");

  // Form state
  const [type, setType] = useState<JournalEntryType>(entry?.type || "daily");
  const [title, setTitle] = useState(entry?.title || "");
  const [content, setContent] = useState(entry?.content || "");
  const [date, setDate] = useState(
    entry?.date
      ? new Date(entry.date).toISOString().slice(0, 10)
      : new Date().toISOString().slice(0, 10)
  );
  const [moodRating, setMoodRating] = useState(entry?.moodRating || undefined);
  const [marketConditions, setMarketConditions] = useState(entry?.marketConditions || "");
  const [lessonsLearned, setLessonsLearned] = useState<string[]>(entry?.lessonsLearned || []);
  const [lessonInput, setLessonInput] = useState("");
  const [goals, setGoals] = useState<string[]>(entry?.goals || []);
  const [goalInput, setGoalInput] = useState("");
  const [tags, setTags] = useState<string[]>(entry?.tags || []);
  const [tagInput, setTagInput] = useState("");
  
  // Psychological Tracking Fields
  const [preTradeConfidence, setPreTradeConfidence] = useState(entry?.preTradeConfidence?.toString() || "");
  const [preTradeEmotion, setPreTradeEmotion] = useState<"calm" | "anxious" | "excited" | "fearful" | "confident" | "uncertain" | undefined>(entry?.preTradeEmotion);
  const [followedPlan, setFollowedPlan] = useState<boolean | undefined>(entry?.followedPlan);
  const [temptedEarlyExit, setTemptedEarlyExit] = useState<boolean | undefined>(entry?.temptedEarlyExit);
  const [addedToPosition, setAddedToPosition] = useState<boolean | undefined>(entry?.addedToPosition);
  const [postTradeEmotion, setPostTradeEmotion] = useState<"satisfied" | "frustrated" | "relieved" | "regretful" | "proud" | "disappointed" | undefined>(entry?.postTradeEmotion);
  const [emotionsInvolved, setEmotionsInvolved] = useState<boolean | undefined>(entry?.emotionsInvolved);

  // Reset form when entry changes
  useEffect(() => {
    if (entry) {
      setType(entry.type);
      setTitle(entry.title);
      setContent(entry.content);
      setDate(new Date(entry.date).toISOString().slice(0, 10));
      setMoodRating(entry.moodRating);
      setMarketConditions(entry.marketConditions || "");
      setLessonsLearned(entry.lessonsLearned || []);
      setGoals(entry.goals || []);
      setTags(entry.tags || []);
      setPreTradeConfidence(entry.preTradeConfidence?.toString() || "");
      setPreTradeEmotion(entry.preTradeEmotion);
      setFollowedPlan(entry.followedPlan);
      setTemptedEarlyExit(entry.temptedEarlyExit);
      setAddedToPosition(entry.addedToPosition);
      setPostTradeEmotion(entry.postTradeEmotion);
      setEmotionsInvolved(entry.emotionsInvolved);
    } else {
      // Reset to defaults
      setType("daily");
      setTitle("");
      setContent("");
      setDate(new Date().toISOString().slice(0, 10));
      setMoodRating(undefined);
      setMarketConditions("");
      setLessonsLearned([]);
      setGoals([]);
      setTags([]);
      setPreTradeConfidence("");
      setPreTradeEmotion(undefined);
      setFollowedPlan(undefined);
      setTemptedEarlyExit(undefined);
      setAddedToPosition(undefined);
      setPostTradeEmotion(undefined);
      setEmotionsInvolved(undefined);
    }
  }, [entry, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.warning("Title required", { description: "Please enter a title for your journal entry." });
      return;
    }

    if (!content.trim()) {
      toast.warning("Content required", { description: "Please enter some content for your journal entry." });
      return;
    }

    setIsSubmitting(true);

    const formData: JournalEntryFormData = {
      type,
      title: title.trim(),
      content: content.trim(),
      date,
      relatedTradeIds: entry?.relatedTradeIds || [],
      tags,
      lessonsLearned,
      moodRating,
      marketConditions: marketConditions.trim() || undefined,
      goals,
      preTradeConfidence: preTradeConfidence ? parseFloat(preTradeConfidence) : undefined,
      preTradeEmotion: preTradeEmotion || undefined,
      followedPlan: followedPlan !== undefined ? followedPlan : undefined,
      temptedEarlyExit: temptedEarlyExit !== undefined ? temptedEarlyExit : undefined,
      addedToPosition: addedToPosition !== undefined ? addedToPosition : undefined,
      postTradeEmotion: postTradeEmotion || undefined,
      emotionsInvolved: emotionsInvolved !== undefined ? emotionsInvolved : undefined,
    };

    onSave(formData);
    setIsSubmitting(false);
  };

  const handleAddLesson = () => {
    if (lessonInput.trim() && !lessonsLearned.includes(lessonInput.trim())) {
      setLessonsLearned([...lessonsLearned, lessonInput.trim()]);
      setLessonInput("");
    }
  };

  const handleRemoveLesson = (index: number) => {
    setLessonsLearned(lessonsLearned.filter((_, i) => i !== index));
  };

  const handleAddGoal = () => {
    if (goalInput.trim() && !goals.includes(goalInput.trim())) {
      setGoals([...goals, goalInput.trim()]);
      setGoalInput("");
    }
  };

  const handleRemoveGoal = (index: number) => {
    setGoals(goals.filter((_, i) => i !== index));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput("");
    }
  };

  const handleRemoveTag = (index: number) => {
    setTags(tags.filter((_, i) => i !== index));
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      size="3xl"
      scrollBehavior="inside" 
      isCentered
    >
      <ModalOverlay backdropFilter="blur(10px)" bg="rgba(0, 0, 0, 0.1)" />
      <ModalContent maxH="90vh">
        <ModalHeader color="#38BBD1" fontFamily="'Urbanist', sans-serif" fontWeight="normal">
          {entry ? "Edit Journal Entry" : "New Journal Entry"}
        </ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit}>
          <ModalBody overflowY="auto" maxH="calc(90vh - 140px)">
            <VStack spacing={6} align="stretch">
              <FormControl isRequired>
                <FormLabel>Entry Type</FormLabel>
                <RadioGroup value={type} onChange={(value) => setType(value as JournalEntryType)}>
                  <Stack direction="row" spacing={4}>
                    <Radio value="daily">Daily</Radio>
                    <Radio value="weekly">Weekly</Radio>
                    <Radio value="trade_review">Trade Review</Radio>
                    <Radio value="reflection">Reflection</Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Title</FormLabel>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter entry title..."
                    bg={bgColor}
                    borderColor={borderColor}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Date</FormLabel>
                  <Input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    bg={bgColor}
                    borderColor={borderColor}
                  />
                </FormControl>
              </SimpleGrid>

              <FormControl isRequired>
                <FormLabel>Content</FormLabel>
                <Textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Write your journal entry here..."
                  rows={6}
                  bg={bgColor}
                  borderColor={borderColor}
                />
              </FormControl>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel>Mood Rating (1-5)</FormLabel>
                  <NumberInput
                    value={moodRating}
                    onChange={(_, value) => setMoodRating(isNaN(value) ? undefined : value)}
                    min={1}
                    max={5}
                  >
                    <NumberInputField bg={bgColor} borderColor={borderColor} />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel>Market Conditions</FormLabel>
                  <Textarea
                    value={marketConditions}
                    onChange={(e) => setMarketConditions(e.target.value)}
                    placeholder="Describe current market conditions..."
                    rows={3}
                    bg={bgColor}
                    borderColor={borderColor}
                  />
                </FormControl>
              </SimpleGrid>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel>Lessons Learned</FormLabel>
                  <HStack mb={2}>
                    <Input
                      value={lessonInput}
                      onChange={(e) => setLessonInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddLesson();
                        }
                      }}
                      placeholder="Add a lesson learned..."
                      bg={bgColor}
                      borderColor={borderColor}
                    />
                    <Button size="sm" onClick={handleAddLesson}>
                      Add
                    </Button>
                  </HStack>
                  {lessonsLearned.length > 0 && (
                    <HStack flexWrap="wrap" spacing={2} mt={2}>
                      {lessonsLearned.map((lesson, index) => (
                        <Badge
                          key={index}
                          colorScheme="teal"
                          px={3}
                          py={1}
                          borderRadius="full"
                          display="flex"
                          alignItems="center"
                          gap={2}
                        >
                          {lesson}
                          <IconButton
                            aria-label="Remove lesson"
                            icon={<AiOutlineClose />}
                            size="xs"
                            variant="ghost"
                            onClick={() => handleRemoveLesson(index)}
                          />
                        </Badge>
                      ))}
                    </HStack>
                  )}
                </FormControl>

                <FormControl>
                  <FormLabel>Goals</FormLabel>
                  <HStack mb={2}>
                    <Input
                      value={goalInput}
                      onChange={(e) => setGoalInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddGoal();
                        }
                      }}
                      placeholder="Add a goal..."
                      bg={bgColor}
                      borderColor={borderColor}
                    />
                    <Button size="sm" onClick={handleAddGoal}>
                      Add
                    </Button>
                  </HStack>
                  {goals.length > 0 && (
                    <HStack flexWrap="wrap" spacing={2} mt={2}>
                      {goals.map((goal, index) => (
                        <Badge
                          key={index}
                          colorScheme="purple"
                          px={3}
                          py={1}
                          borderRadius="full"
                          display="flex"
                          alignItems="center"
                          gap={2}
                        >
                          {goal}
                          <IconButton
                            aria-label="Remove goal"
                            icon={<AiOutlineClose />}
                            size="xs"
                            variant="ghost"
                            onClick={() => handleRemoveGoal(index)}
                          />
                        </Badge>
                      ))}
                    </HStack>
                  )}
                </FormControl>
              </SimpleGrid>

              <FormControl>
                <FormLabel>Tags</FormLabel>
                <HStack mb={2}>
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddTag();
                      }
                    }}
                    placeholder="Add a tag..."
                    bg={bgColor}
                    borderColor={borderColor}
                  />
                  <Button size="sm" onClick={handleAddTag}>
                    Add
                  </Button>
                </HStack>
                {tags.length > 0 && (
                  <HStack flexWrap="wrap" spacing={2} mt={2}>
                    {tags.map((tag, index) => (
                      <Badge
                        key={index}
                        colorScheme="blue"
                        px={3}
                        py={1}
                        borderRadius="full"
                        display="flex"
                        alignItems="center"
                        gap={2}
                      >
                        {tag}
                        <IconButton
                          aria-label="Remove tag"
                          icon={<AiOutlineClose />}
                          size="xs"
                          variant="ghost"
                          onClick={() => handleRemoveTag(index)}
                        />
                      </Badge>
                    ))}
                  </HStack>
                )}
              </FormControl>

              {/* Psychological Tracking Section */}
              <Box>
                <Text
                  fontSize="md"
                  fontWeight="semibold"
                  color={textColor}
                  mb={4}
                  fontFamily="'Urbanist', sans-serif"
                >
                  Psychological Tracking
                </Text>
                <Divider mb={4} />
                
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel>Pre-Trade Confidence (1-10)</FormLabel>
                    <NumberInput
                      value={preTradeConfidence}
                      onChange={(_, value) => setPreTradeConfidence(isNaN(value) ? "" : value.toString())}
                      min={1}
                      max={10}
                    >
                      <NumberInputField bg={bgColor} borderColor={borderColor} placeholder="1-10" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Pre-Trade Emotion</FormLabel>
                    <Select
                      value={preTradeEmotion || ""}
                      onChange={(e) => setPreTradeEmotion(e.target.value as typeof preTradeEmotion || undefined)}
                      bg={bgColor}
                      borderColor={borderColor}
                      placeholder="Select emotion"
                    >
                      <option value="calm">Calm</option>
                      <option value="anxious">Anxious</option>
                      <option value="excited">Excited</option>
                      <option value="fearful">Fearful</option>
                      <option value="confident">Confident</option>
                      <option value="uncertain">Uncertain</option>
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Post-Trade Emotion</FormLabel>
                    <Select
                      value={postTradeEmotion || ""}
                      onChange={(e) => setPostTradeEmotion(e.target.value as typeof postTradeEmotion || undefined)}
                      bg={bgColor}
                      borderColor={borderColor}
                      placeholder="Select emotion"
                    >
                      <option value="satisfied">Satisfied</option>
                      <option value="frustrated">Frustrated</option>
                      <option value="relieved">Relieved</option>
                      <option value="regretful">Regretful</option>
                      <option value="proud">Proud</option>
                      <option value="disappointed">Disappointed</option>
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Plan Adherence</FormLabel>
                    <RadioGroup
                      value={followedPlan === true ? "yes" : followedPlan === false ? "no" : ""}
                      onChange={(value) => setFollowedPlan(value === "yes" ? true : value === "no" ? false : undefined)}
                    >
                      <Stack direction="row" spacing={4}>
                        <Radio value="yes">Followed Plan</Radio>
                        <Radio value="no">Deviated</Radio>
                      </Stack>
                    </RadioGroup>
                  </FormControl>
                </SimpleGrid>

                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mt={4}>
                  <FormControl>
                    <Checkbox
                      isChecked={emotionsInvolved === true}
                      onChange={(e) => setEmotionsInvolved(e.target.checked ? true : undefined)}
                      colorScheme="blue"
                    >
                      Emotions Involved
                    </Checkbox>
                  </FormControl>

                  <FormControl>
                    <Checkbox
                      isChecked={temptedEarlyExit === true}
                      onChange={(e) => setTemptedEarlyExit(e.target.checked ? true : undefined)}
                      colorScheme="blue"
                    >
                      Tempted Early Exit
                    </Checkbox>
                  </FormControl>

                  <FormControl>
                    <Checkbox
                      isChecked={addedToPosition === true}
                      onChange={(e) => setAddedToPosition(e.target.checked ? true : undefined)}
                      colorScheme="blue"
                    >
                      Added to Position
                    </Checkbox>
                  </FormControl>
                </SimpleGrid>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={onClose}
              isDisabled={isSubmitting}
              fontFamily="'IBM Plex Sans', sans-serif"
              fontSize="xs"
              color="gray.400"
              fontWeight="medium"
              textTransform="uppercase"
            >
              Cancel
            </Button>
            <Button
              bgGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
              color="white"
              type="submit"
              isLoading={isSubmitting}
              loadingText="Saving..."
              fontFamily="'IBM Plex Sans', sans-serif"
              fontSize="xs"
              fontWeight="medium"
              textTransform="uppercase"
              _hover={{
                bgGradient: "linear-gradient(135deg, #38BBD1, #3EEAEC)",
                opacity: 0.9,
              }}
            >
              {entry ? "Update Entry" : "Save Entry"}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default JournalEntryModal;

